import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { applyHighlightsToElement } from './highlight';

export function SpreadsheetViewer({ url, height = 'calc(85vh - 44px)', highlightText }: { url: string; height?: string | number; highlightText?: string | null }) {
  const [html, setHtml] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setHtml('');

    (async () => {
      try {
        const resp = await fetch(url);
        if (!resp.ok) throw new Error('Failed to fetch spreadsheet');
        const buf = await resp.arrayBuffer();

        const wb = XLSX.read(buf, { type: 'array' });
        const firstName = wb.SheetNames?.[0];
        if (!firstName) throw new Error('No sheets found');
        const sheet: any = wb.Sheets?.[firstName];
        if (!sheet) throw new Error('Failed to read worksheet');

        // Some files (especially certain .xls exports) may not have a '!ref'.
        // SheetJS helpers like sheet_to_html assume it exists and can throw
        // "Cannot read properties of undefined (reading 'indexOf')".
        if (!sheet['!ref']) {
          const cellAddrs = Object.keys(sheet).filter(k => k && k[0] !== '!');
          if (!cellAddrs.length) throw new Error('Worksheet is empty');

          let minR = Number.POSITIVE_INFINITY;
          let minC = Number.POSITIVE_INFINITY;
          let maxR = 0;
          let maxC = 0;

          for (const addr of cellAddrs) {
            try {
              const decoded = XLSX.utils.decode_cell(addr);
              if (decoded.r < minR) minR = decoded.r;
              if (decoded.c < minC) minC = decoded.c;
              if (decoded.r > maxR) maxR = decoded.r;
              if (decoded.c > maxC) maxC = decoded.c;
            } catch {
              // Ignore non-cell keys
            }
          }

          if (!Number.isFinite(minR) || !Number.isFinite(minC)) throw new Error('Worksheet range is invalid');
          sheet['!ref'] = XLSX.utils.encode_range({ s: { r: minR, c: minC }, e: { r: maxR, c: maxC } });
        }

        // Convert to HTML table.
        const tableHtml = XLSX.utils.sheet_to_html(sheet, { id: 'sheet-table' });
        if (cancelled) return;
        setHtml(tableHtml);
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.message || 'Failed to load spreadsheet');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [url]);

  useEffect(() => {
    if (!contentRef.current) return;
    if (!html) return;
    applyHighlightsToElement(contentRef.current, highlightText);
  }, [html, highlightText]);

  if (error) return <div style={{ padding: 12, color: 'red' }}>{error}</div>;
  if (!html) return <div style={{ padding: 12 }}>Loading spreadsheet…</div>;

  return (
    <div style={{ height, overflow: 'auto', padding: 12 }}>
      <div
        ref={contentRef}
        style={{ fontSize: 13 }}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  );
}
